import { useState, useEffect } from 'react';
import { getStudentData, saveStudentData } from '../assets/data';
import {
  getLessonById,
  saveLesson,
  deleteLesson,
  getLessonListForStudent,
  getLessonsFromLocalStorage,
  saveLessonsToLocalStorage,
} from '../utils/lessonsService';
import { getStudents as getStudentsFromSupabase } from '../utils/studentsService';

function normalizeDateForInput(dateVal) {
  if (!dateVal) return new Date().toISOString().split('T')[0];
  const str = String(dateVal);
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  const d = new Date(str);
  if (!isNaN(d.getTime())) return d.toISOString().split('T')[0];
  return new Date().toISOString().split('T')[0];
}

// Extracted from StudentProfile.js (Phase 3 of the platform-health plan:
// getting the file under ~600 lines needs pulling logic into hooks, not
// just JSX into components). Owns the Lessons tab's CRUD actions: opening
// a lesson, deleting one, and copying one to another student (a modal
// flow of its own, since it needs to pick a target student + date and
// re-fetch the full lesson content before saving the copy).
export function useLessonsManager({ studentId, isNew, navigate, setStudentLessons, showCustomModalConfig, setShowCustomModal }) {
  const [deletingLessonId, setDeletingLessonId] = useState(null);

  const [showCopyModal, setShowCopyModal] = useState(false);
  const [lessonToCopy, setLessonToCopy] = useState(null);
  const [copyTargetStudent, setCopyTargetStudent] = useState('');
  const [copyDate, setCopyDate] = useState('');
  const [copyModalStudents, setCopyModalStudents] = useState(null); // студенты для выбора в Copy modal (Supabase + local)
  const [copyLessonSubmitting, setCopyLessonSubmitting] = useState(false);

  const handleStartLesson = (lessonId) => {
    navigate(`/lessons/${lessonId}?studentId=${studentId}`);
  };

  // Функция для удаления урока
  const handleDeleteLesson = (lessonId) => {
    showCustomModalConfig({
      title: 'Delete Lesson',
      message: 'Are you sure you want to delete this lesson? This action cannot be undone.',
      type: 'warning',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      showCancel: true,
      onConfirm: async () => {
        setShowCustomModal(false);
        setDeletingLessonId(lessonId);
        try {
          await deleteLesson(lessonId);
        } catch (err) {
          console.error('Error deleting lesson from Supabase:', err);
          setDeletingLessonId(null);
          showCustomModalConfig({
            title: 'Error',
            message: err?.message || 'Failed to delete lesson.',
            type: 'error',
            confirmText: 'OK'
          });
          return;
        }
        // Удаляем урок из списка уроков студента
        setStudentLessons(prev => prev.filter(lesson => lesson.id !== lessonId));

        // Удаляем из основного хранилища novas_lessons_data
        const allLessons = getLessonsFromLocalStorage();
        const updatedLessons = allLessons.filter(lesson => lesson.id !== lessonId);
        saveLessonsToLocalStorage(updatedLessons);

        // Удаляем урок из профиля студента, если он там есть
        const localStudentRecord = getStudentData()[studentId];
        if (localStudentRecord && localStudentRecord.lessons) {
          const updatedStudentData = {
            ...localStudentRecord,
            lessons: localStudentRecord.lessons.filter(lesson => lesson.id !== lessonId)
          };

          const allStudents = getStudentData();
          allStudents[studentId] = updatedStudentData;
          saveStudentData(allStudents);
        }

        console.log('Lesson deleted:', lessonId);

        // Принудительно обновляем список уроков после удаления
        if (studentId && studentId !== "new" && !isNew) {
          const currentStudent = getStudentData()[studentId];
          const studentIdForLessons = currentStudent?.studentId || currentStudent?.id || studentId;
          if (studentIdForLessons) {
            setTimeout(() => {
              getLessonListForStudent(studentIdForLessons).then(lessons => {
                setStudentLessons(lessons);
              }).catch(error => {
                console.error('Error loading lessons:', error);
              });
            }, 100);
          }
        }

        // Закрываем модальное окно
        setDeletingLessonId(null);
      }
    });
  };

  // Функция для копирования урока
  const handleCopyLesson = (lesson) => {
    setLessonToCopy(lesson);
    setCopyTargetStudent('');
    setCopyDate(normalizeDateForInput(lesson?.date));
    setShowCopyModal(true);
  };

  // При открытии Copy modal подгружаем всех студентов из Supabase, чтобы видеть полный список
  useEffect(() => {
    if (!showCopyModal) return;
    getStudentsFromSupabase()
      .then((supabaseStudents) => {
        setCopyModalStudents(supabaseStudents || {});
      })
      .catch(() => setCopyModalStudents({}));
  }, [showCopyModal]);

  // Функция для подтверждения копирования урока
  const confirmCopyLesson = async () => {
    if (!copyTargetStudent || copyTargetStudent === '') {
      showCustomModalConfig({
        title: 'Error',
        message: 'Please select a target student.',
        type: 'error'
      });
      return;
    }

    if (!copyDate) {
      showCustomModalConfig({
        title: 'Error',
        message: 'Please select a date for the copied lesson.',
        type: 'error'
      });
      return;
    }

    if (!lessonToCopy) {
      showCustomModalConfig({
        title: 'Error',
        message: 'No lesson selected for copying.',
        type: 'error'
      });
      return;
    }

    setCopyLessonSubmitting(true);

    // Получаем список всех студентов (local + Supabase), чтобы выбранный студент был в списке
    const students = { ...getStudentData(), ...(copyModalStudents || {}) };

    // Загружаем полный урок из Supabase (список уроков хранит только метаданные без контента)
    let originalLesson = null;
    try {
      originalLesson = await getLessonById(lessonToCopy.id);
    } catch (e) {
      console.warn('getLessonById failed, falling back to lessonToCopy:', e);
    }
    if (!originalLesson) {
      originalLesson = lessonToCopy;
    }

    // Получаем UUID студента (если copyTargetStudent - это slug, нужно конвертировать)
    let targetStudentId = copyTargetStudent;
    const targetStudent = students[copyTargetStudent];
    if (targetStudent?.studentId) {
      targetStudentId = targetStudent.studentId; // Используем UUID если есть
    } else if (targetStudent?.id) {
      targetStudentId = targetStudent.id;
    }

    // Создаем копию урока с полным содержимым
    const newLesson = {
      ...originalLesson,
      id: 'new', // Будет создан новый ID в Supabase
      studentId: targetStudentId, // UUID студента
      date: copyDate,
      status: 'in_progress', // Сбрасываем статус
      // Копируем полное содержимое урока
      containers: originalLesson?.containers || lessonToCopy.containers || [{ id: 1, type: 'editor' }],
      whiteboards: originalLesson?.whiteboards || lessonToCopy.whiteboards || [],
      editorContents: originalLesson?.editorContents || lessonToCopy.editorContents || [],
      whiteboardContents: originalLesson?.whiteboardContents || lessonToCopy.whiteboardContents || [],
      // Копируем другие важные поля
      docContainers: originalLesson?.docContainers || lessonToCopy.docContainers || [],
      docEditorContents: originalLesson?.docEditorContents || lessonToCopy.docEditorContents || [],
      homeworkContainers: originalLesson?.homeworkContainers || lessonToCopy.homeworkContainers || [],
      homeworkEditorContents: originalLesson?.homeworkEditorContents || lessonToCopy.homeworkEditorContents || [],
      bannerImage: originalLesson?.bannerImage || lessonToCopy.bannerImage || null,
      docBanners: originalLesson?.docBanners || lessonToCopy.docBanners || {},
      homeworkBanners: originalLesson?.homeworkBanners || lessonToCopy.homeworkBanners || {},
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    try {
      await saveLesson(newLesson);

      // Обновляем список уроков для текущего студента, если копируем в его профиль
      if (copyTargetStudent === studentId) {
        const currentStudent = getStudentData()[studentId];
        const studentIdForLessons = currentStudent?.studentId || currentStudent?.id || studentId;
        const updatedLessons = await getLessonListForStudent(studentIdForLessons);
        setStudentLessons(updatedLessons);
      }

      setCopyLessonSubmitting(false);
      // Закрываем модальное окно
      setShowCopyModal(false);
      setLessonToCopy(null);
      setCopyTargetStudent('');
      setCopyDate('');

      showCustomModalConfig({
        title: '✅ Lesson Copied Successfully!',
        message: `Lesson "${lessonToCopy.title}" has been copied to ${students[copyTargetStudent]?.name || copyTargetStudent}'s profile successfully!\n\nYou can now find this lesson in the student's profile under the Lessons tab.`,
        type: 'success',
        confirmText: 'OK',
        onConfirm: () => {
          // Перезагружаем уроки для обновления списка
          const currentStudent = getStudentData()[studentId];
          const studentIdForLessons = currentStudent?.studentId || currentStudent?.id || studentId;
          getLessonListForStudent(studentIdForLessons).then(lessons => {
            setStudentLessons(lessons);
          }).catch(error => {
            console.error('Error reloading lessons:', error);
          });
        }
      });
    } catch (error) {
      setCopyLessonSubmitting(false);
      console.error('Error saving lesson to Supabase:', error);
      showCustomModalConfig({
        title: 'Error',
        message: `Failed to copy lesson: ${error.message || 'Unknown error'}`,
        type: 'error'
      });
    }
  };

  return {
    deletingLessonId,
    handleStartLesson,
    handleDeleteLesson,
    handleCopyLesson,
    confirmCopyLesson,
    showCopyModal, setShowCopyModal,
    lessonToCopy,
    copyTargetStudent, setCopyTargetStudent,
    copyDate, setCopyDate,
    copyModalStudents,
    copyLessonSubmitting,
  };
}
